import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, apiFetch } from './api-client';
import { telegram } from './telegram';

/**
 * Timestamp back terakhir — cegah double-pop bila back terpicu ganda dalam
 * satu gestur (mis. handler native Telegram + tombol in-app, atau handler
 * yang sempat menumpuk). Dua panggilan < 200ms dianggap 1 aksi.
 */
let lastBackAt = 0;

/**
 * Kembali ke halaman SEBELUMNYA yang sesungguhnya (bukan selalu ke home).
 *
 * React Router menyimpan indeks entri navigasi di `window.history.state.idx`:
 *   - idx > 0  → ada halaman sebelumnya dalam sesi app → `navigate(-1)`.
 *   - idx == 0 → entri pertama (mis. dibuka langsung via deep-link /
 *     dari Telegram) → fallback ke home supaya tidak keluar app.
 */
export function useGoBack(): () => void {
  const navigate = useNavigate();
  return useCallback(() => {
    const now = Date.now();
    if (now - lastBackAt < 200) return; // buang picuan ganda
    lastBackAt = now;
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [navigate]);
}

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
}

interface WhoamiResponse {
  user: TelegramUser | null;
  authenticated: boolean;
}

/**
 * Tanyakan backend siapa kita. Return `null` bila belum login (browser
 * biasa) atau bila initData tidak valid.
 *
 * Query enabled hanya bila kita punya initData di client — jangan
 * hit backend untuk hasil yg pasti anonymous.
 */
export function useCurrentUser() {
  const initData = telegram.initData;
  return useQuery({
    queryKey: ['whoami', initData.length > 0 ? initData.slice(0, 16) : 'anon'],
    queryFn: () => apiFetch<WhoamiResponse>('/whoami'),
    staleTime: 5 * 60_000,
    retry: (count, err) =>
      !(err instanceof ApiError && err.status >= 400 && err.status < 500) &&
      count < 1,
  });
}
